<script setup lang="ts">
/**
 * 재사용 날짜 입력 — 동적 배열 행(allottee 등)에서 v-model 로 사용.
 *   패널의 다른 날짜 입력과 동일하게 useDateField(초록 달력 + 직접 텍스트 입력 + 세션 dateFormat 표시) 사용.
 *   v-model 은 내부 저장 형식 "YYYY-MM-DD" 문자열.
 *
 * skippable: 필드 안 X(prepend) 로 skip 가능 (Resolution/Allotment 날짜와 동일 UX).
 *   skip 상태는 부모가 :skipped 로 제어, X/Restore 클릭 시 @update:skipped 로 토글 요청.
 */
import { watch } from 'vue';
import { useDateField } from '@/shared/composables/dateUtils';

const props = withDefaults(defineProps<{
  modelValue?: string;
  label?: string;
  skippable?: boolean;
  skipped?: boolean;
}>(), { modelValue: '', label: '', skippable: false, skipped: false });

const emit = defineEmits<{
  'update:modelValue': [v: string];
  'update:skipped': [v: boolean];
}>();

const f = useDateField(props.modelValue || '');
const dateValue = f.dateValue;
const localText = f.localText;
const datePlaceholder = f.datePlaceholder;
const dateMenu = f.dateMenu;
const datePickerValue = f.datePickerValue;
const datePickerProps = f.datePickerProps;
const onDateTextBlur = f.onDateTextBlur;

// 외부(prefill) → 내부
watch(() => props.modelValue, (v) => {
  if ((v || '') !== (dateValue.value || '')) dateValue.value = v || '';
});
// 내부(달력/입력) → 외부
watch(dateValue, (v) => {
  if ((v || '') !== (props.modelValue || '')) emit('update:modelValue', v || '');
});
</script>

<template>
  <v-text-field
    v-model="localText"
    :placeholder="datePlaceholder"
    :label="label"
    variant="outlined" density="compact" hide-details="auto"
    :class="{ 'co-field-skipped': skippable && skipped }"
    @blur="onDateTextBlur($event.target.value)"
  >
    <!-- skip X — 필드 안 좌측 (skip 가능 + 아직 skip 아닐 때) -->
    <template v-if="skippable && !skipped" #prepend-inner>
      <v-tooltip location="top" :open-delay="150">
        <template #activator="{ props: tipProps }">
          <span v-bind="tipProps" class="co-date-skip-btn" @mousedown.prevent @click.stop="emit('update:skipped', true)">
            <v-icon size="14" color="grey-lighten-1">mdi-close</v-icon>
          </span>
        </template>
        Skip — fill before lodging
      </v-tooltip>
    </template>

    <template #append-inner>
      <a v-if="skippable && skipped" class="co-date-restore-link" @click.stop="emit('update:skipped', false)">Restore</a>
      <v-menu v-else v-model="dateMenu" :close-on-content-click="false" location="bottom">
        <template #activator="{ props: menuProps }">
          <v-icon v-bind="menuProps" color="success" size="20" style="cursor:pointer">mdi-calendar</v-icon>
        </template>
        <v-date-picker
          v-bind="datePickerProps"
          v-model="datePickerValue"
          @update:model-value="dateMenu = false"
        />
      </v-menu>
    </template>
  </v-text-field>
</template>

<style scoped>
.co-date-skip-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 18px; height: 18px; border-radius: 4px; cursor: pointer; margin-right: 2px;
}
.co-date-skip-btn:hover { background: rgba(244, 67, 54, 0.12); }
.co-date-skip-btn:hover :deep(.v-icon) { color: #f44336 !important; }
.co-date-restore-link { font-size: 12px; color: #1565c0; cursor: pointer; text-decoration: underline; white-space: nowrap; }
.co-date-restore-link:hover { color: #0d47a1; }
.co-field-skipped :deep(.v-field) { background: #fafafa; opacity: 0.85; }
</style>
